import { useCallback, useContext } from "react";
import { SDLCMetadataConstants, } from "@sscs/types";
import { GRADE_TO_PRIORITY, capitalizeFirstLetter } from "@sscs/utils";
import { ASSESSMENT_TYPE_TO_TITLE, COMPONENT_UNKNOWN_YEAR, categoryToTitle, } from "../../constants";
import { ReportContext } from "../context";
import { useCategoryFilterOptions, useLevels } from "../hooks";
import { useLazyGetComponentAgesListQuery, useLazyGetPathListQuery, useLazyGetPolicyDescriptionListQuery, useLazyGetPublisherListQuery, useLazyGetTagListQuery, } from "../services/issues.api";
import { useGetReportMappingsQuery } from "../services/shared.api";
const getCommonListLabel = (option, list) => { var _a; var _b; return (_b = (_a = list === null || list === void 0 ? void 0 : list.find((item) => item.id === option)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : option; };
const useFilterOptions = () => {
    const { reportId } = useContext(ReportContext);
    const { scanLevel } = useLevels();
    const categoryFilterOptions = useCategoryFilterOptions();
    const { data: reportMappings } = useGetReportMappingsQuery();
    const [fetchIssues, { data: issues, isFetching: isIssuesFetching }] = useLazyGetPolicyDescriptionListQuery();
    const [fetchPaths, { data: paths, isFetching: isPathsFetching }] = useLazyGetPathListQuery();
    const [fetchPublishers, { data: publishers, isFetching: isPublishersFetching },] = useLazyGetPublisherListQuery();
    const [fetchTags, { data: tags, isFetching: isTagsFetching }] = useLazyGetTagListQuery();
    const [fetchComponentAges, { data: componentAges, isFetching: isComponentAgesFetching },] = useLazyGetComponentAgesListQuery();
    return {
        issue: {
            name: "issue",
            label: "Issue",
            component: "autocomplete",
            operations: ["is", "is_not", "is_any", "is_not_any"],
            options: issues === null || issues === void 0 ? void 0 : issues.map((issue) => issue.id),
            isLoading: isIssuesFetching,
            onFilterSelect: useCallback(() => fetchIssues({ ui_id: reportId }), [fetchIssues, reportId]),
            getOptionLabel: (option) => `${option} - ${reportMappings === null || reportMappings === void 0 ? void 0 : reportMappings.descriptions[option]}`,
            getChipLabel: (option) => option,
        },
        policy_category: {
            name: "policy_category",
            label: "Issue category",
            component: "autocomplete",
            operations: ["is", "is_not", "is_any", "is_not_any"],
            options: categoryFilterOptions.map((category) => category),
            getOptionLabel: (option) => categoryToTitle[option],
        },
        assessment_category: {
            name: "assessment_category",
            label: "Assessment category",
            component: "autocomplete",
            operations: ["is", "is_not", "is_any", "is_not_any"],
            options: Object.keys(ASSESSMENT_TYPE_TO_TITLE),
            getOptionLabel: (option) => ASSESSMENT_TYPE_TO_TITLE[option],
        },
        grade: {
            name: "grade",
            label: "Priority",
            component: "autocomplete",
            operations: ["is", "is_not", "is_any", "is_not_any"],
            // Better UX if priorities are sorted from worst (P0) to least bad (P4)
            options: SDLCMetadataConstants.softwareGrade.toReversed(),
            getOptionLabel: (option) => GRADE_TO_PRIORITY[option],
        },
        file_name: {
            name: "file_name",
            label: "File name",
            component: "input",
            operations: ["is", "is_not"],
        },
        status: {
            name: "status",
            label: "CI/CD status",
            component: "autocomplete",
            operations: ["is"],
            options: SDLCMetadataConstants.releaseStatus.map((item) => item),
            getOptionLabel: (option) => capitalizeFirstLetter(option),
        },
        level: {
            name: "level",
            label: "Level",
            component: "autocomplete",
            operations: ["is"],
            options: [1, 2, 3, 4, 5].map((level) => level.toString()),
            isOptionHidden: scanLevel === 0, // We don't want to offer levels filter if report is not using levels
        },
        component_age: {
            name: "component_age",
            label: "Component age",
            component: "autocomplete",
            operations: ["is", "is_not", "is_any", "is_not_any"],
            options: componentAges === null || componentAges === void 0 ? void 0 : componentAges.map((path) => path.id),
            isLoading: isComponentAgesFetching,
            onFilterSelect: useCallback(() => fetchComponentAges({ ui_id: reportId }), [fetchComponentAges, reportId]),
            getOptionLabel: (option) => option === COMPONENT_UNKNOWN_YEAR ? "Unknown" : option,
        },
        file_path: {
            name: "file_path",
            label: "File path",
            component: "autocomplete",
            operations: ["is", "is_not", "is_any", "is_not_any"],
            options: paths === null || paths === void 0 ? void 0 : paths.map((path) => path.id),
            isLoading: isPathsFetching,
            onFilterSelect: useCallback(() => fetchPaths({ ui_id: reportId }), [fetchPaths, reportId]),
            getOptionLabel: (option) => getCommonListLabel(option, paths),
        },
        publisher: {
            name: "publisher",
            label: "Publisher",
            component: "autocomplete",
            operations: ["is", "is_not", "is_any", "is_not_any"],
            options: publishers === null || publishers === void 0 ? void 0 : publishers.map((publisher) => publisher.id),
            isLoading: isPublishersFetching,
            onFilterSelect: useCallback(() => fetchPublishers({ ui_id: reportId }), [fetchPublishers, reportId]),
            getOptionLabel: (option) => getCommonListLabel(option, publishers),
        },
        tag: {
            name: "tag",
            label: "Tag",
            component: "autocomplete",
            operations: ["is", "is_not", "is_any", "is_not_any"],
            options: tags === null || tags === void 0 ? void 0 : tags.map((tag) => tag.id),
            isLoading: isTagsFetching,
            onFilterSelect: useCallback(() => fetchTags({ ui_id: reportId }), [fetchTags, reportId]),
            getOptionLabel: (option) => getCommonListLabel(option, tags),
        },
    };
};
export default useFilterOptions;
//# sourceMappingURL=useFilterOptions.js.map